/*---------------------------------------------------------------------------------------------
 *  Copyright (c) i-Bridge Systems Philippines Inc. All rights reserved.
 *--------------------------------------------------------------------------------------------*/
import {
  ToggleColorMode,
  CustomTextField,
  Footer,
  OtpModal,
} from '../components/index';
import {
  Box,
  FormGroup,
  Checkbox,
  FormControlLabel,
  Link,
} from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { useState } from 'react';
import { apiUrl } from '../utilities/apiUrl';
import logo from '../assets/images/logo_white.png';
import { useAlert } from '../providers/AlertContext';

const style = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexDirection: 'column',
  position: 'absolute',
  top: '45%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
};

const loginBox = {
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  flexDirection: 'column',
  borderRadius: '5px',
  bgcolor: '#1A202C',
  py: 6,
  px: 8,
  width: '70%',
  boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
};

const buttonStyle = {
  color: '#FFF',
  bgcolor: '#84d8c5',
  height: '2.8rem',
  boxShadow: 'rgba(0, 0, 0, 0.16) 0px 1px 4px',
  '&:hover': {
    bgcolor: '#57af9b !important',
    borderColor: '#57af9b',
  },
};

export const SignIn = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [validUsername, setValidUsername] = useState(true);
  const [validPassword, setValidPassword] = useState(true);
  const [isSending, setIsSending] = useState(false);
  const [otpModalOpen, setOtpModalOpen] = useState(false);
  const [empId, setEmpId] = useState(false);
  const { showAlert } = useAlert();

  /**
   * Checks if keypress corresponds to the Enter key. Calls the form submission function if so.
   *
   * @param {*} e Event passed by a certain action (button click, key press, etc.)
   */
  const handleEnterInput = (e) => {
    if (e.key === 'Enter') {
      handleSubmit(e);
    }
  };

  /**
   * Handles Log In form submission. Sends a POST request to the Login endpoint with given credentials.
   *
   * @param {*} e Event passed by a certain action (button click, key press, etc.)
   */
  const handleSubmit = (e) => {
    e.preventDefault();

    const credentials = {
      username: username,
      password: password,
    };

    //  check if form has values
    if (username === '' || password === '') {
      if (username === '') setValidUsername(false);
      if (password === '') setValidPassword(false);

      return;
    }

    //  set submit button to the processing state
    setIsSending(true);

    //  send POST request
    fetch(apiUrl('/auth/login'), {
      method: 'POST',
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(credentials),
    })
      .then((response) => response.json())
      .then((data) => {
        if (data.success) {
          if (data.twoFa) {
            setOtpModalOpen(true);
            setEmpId(data._id);
            setIsSending(false);
            return;
          }

          window.location.replace('/dashboard');
        } else {
          if (data.message === 'Error. Employee not found')
            setValidUsername(false);

          if (data.message === 'Error. Wrong password') setValidPassword(false);

          if (data.message === 'Error. Account deactivated or locked') {
            showAlert(
              'Your account has been locked. Please contact the administrator to regain access.',
              'error',
              'Account Locked'
            );
          }
        }

        setIsSending(false);
      });
  };

  return (
    <ToggleColorMode mode={'dark'}>
      <Box sx={style}>
        <Box sx={loginBox}>
          <img
            src={logo}
            style={{ marginBottom: '1.6rem' }}
            width="50%"
            alt="i-Bridge Logo"
          />
          <CustomTextField
            type="text"
            label="Username"
            error={!validUsername}
            onKeyDown={handleEnterInput}
            errorMsg="Invalid username."
            required={true}
            onChange={(e) => {
              setUsername(e.target.value);
              if (e.target.value.length === 0) setValidUsername(false);
              else setValidUsername(true);
            }}
          />
          <CustomTextField
            type="password"
            label="Password"
            error={!validPassword}
            errorMsg="Invalid password. Please try again."
            onKeyDown={handleEnterInput}
            required={true}
            onChange={(e) => {
              setPassword(e.target.value);
              if (e.target.value.length === 0) setValidPassword(false);
              else setValidPassword(true);
            }}
          />
          <FormGroup
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              mb: '2rem',
            }}
          >
            {/*[EPSv5_Login-ForgotPasswordAndRememberMe]
            <FormControlLabel control={<Checkbox />} label="Remember me" />
            <Link sx={{ color: "#84d8c5" }} underline="none">
              Forgot password?
            </Link>*/}
          </FormGroup>

          <LoadingButton
            onClick={handleSubmit}
            variant="contained"
            loading={isSending}
            loadingIndicator="Logging in..."
            fullWidth
            sx={buttonStyle}
          >
            Login
          </LoadingButton>
        </Box>
      </Box>
      <Footer style={{ transform: 'translate(-50%, 0)', mb: 2 }} />
      <OtpModal
        open={otpModalOpen}
        onClose={() => setOtpModalOpen(false)}
        empId={empId}
      />
    </ToggleColorMode>
  );
};
